import { Card, StatusBadge } from "@leylion/design-system";
import type { ShiftSummary } from "../api/cashier-types";
import type { ServiceType } from "../api/types";

type Language = "ar" | "en";

type Props = {
  summary: ShiftSummary;
  language: Language;
  formatMoney: (minor: number, currency?: string) => string;
};

const serviceLabels: Record<ServiceType, { ar: string; en: string }> = {
  WalkIn: { ar: "استلام", en: "Pickup" },
  DineIn: { ar: "داخلي", en: "Dine-in" },
  Takeaway: { ar: "سفري", en: "Takeaway" },
  Delivery: { ar: "توصيل", en: "Delivery" }
};

const paymentLabels = {
  Cash: { ar: "نقدي", en: "Cash" },
  Card: { ar: "بطاقة / شبكة", en: "Card / Terminal" },
  GiftCard: { ar: "بطاقة هدية", en: "Gift card" },
  Wallet: { ar: "محفظة", en: "Wallet" },
  Account: { ar: "حساب آجل", en: "On account" }
} as const;

export function CashierShiftSummaryV2({ summary, language, formatMoney }: Props) {
  const ar = language === "ar";
  const activeServices = summary.serviceTypes.filter((item) => item.orderCount > 0 || item.grossMinor !== 0);
  const activePayments = summary.paymentMethods.filter((item) => item.paymentCount > 0 || item.totalCollectionsMinor !== 0 || item.creditSalesMinor !== 0 || item.refundsMinor !== 0 || (item.giftCardForfeitMinor ?? 0) > 0);

  return (
    <div className="cashier-shift-summary-v2" data-testid="shift-detailed-summary">
      <Card title={ar ? "نظرة شاملة على الوردية" : "Shift overview"}>
        <div className="cashier-shift-summary-v2__metrics">
          <span><small>{ar ? "عدد الطلبات" : "Orders"}</small><strong>{summary.orderCount}</strong></span>
          <span><small>{ar ? "عدد الدفعات" : "Payments"}</small><strong>{summary.paymentCount}</strong></span>
          <span><small>{ar ? "صافي المبيعات" : "Net sales"}</small><strong>{formatMoney(summary.netSalesMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "الضريبة" : "VAT"}</small><strong>{formatMoney(summary.taxCollectedMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "إجمالي المبيعات" : "Gross sales"}</small><strong>{formatMoney(summary.grossSalesMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "إجمالي المقبوضات" : "Collections"}</small><strong>{formatMoney(summary.totalCollectionsMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "إجمالي الاستردادات" : "Refunds"}</small><strong>{formatMoney(summary.totalRefundsMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "صافي الإيرادات" : "Net revenue"}</small><strong>{formatMoney(summary.netRevenueMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "الرصيد الافتتاحي" : "Opening float"}</small><strong>{formatMoney(summary.openingCashMinor, summary.currency)}</strong></span>
          <span><small>{ar ? "النقد المتوقع" : "Expected cash"}</small><strong>{formatMoney(summary.expectedCashMinor, summary.currency)}</strong></span>
        </div>
      </Card>

      <Card title={ar ? "المبيعات حسب نوع الطلب" : "Sales by order type"}>
        <div className="cashier-shift-summary-v2__table">
          <div className="is-head">
            <span>{ar ? "النوع" : "Type"}</span><span>{ar ? "الطلبات" : "Orders"}</span><span>{ar ? "الصافي" : "Net"}</span><span>{ar ? "الضريبة" : "VAT"}</span><span>{ar ? "الإجمالي" : "Gross"}</span>
          </div>
          {activeServices.length ? activeServices.map((item) => (
            <div key={item.serviceType}>
              <strong>{serviceLabels[item.serviceType][ar ? "ar" : "en"]}</strong>
              <span>{item.orderCount}</span>
              <span>{formatMoney(item.netMinor, summary.currency)}</span>
              <span>{formatMoney(item.taxMinor, summary.currency)}</span>
              <strong>{formatMoney(item.grossMinor, summary.currency)}</strong>
            </div>
          )) : <small>{ar ? "لا توجد مبيعات مكتملة حتى الآن." : "No completed sales yet."}</small>}
        </div>
      </Card>

      <Card title={ar ? "طرق الدفع والتحصيل" : "Payment methods & collections"}>
        <div className="cashier-shift-summary-v2__payment-list">
          {activePayments.length ? activePayments.map((item) => (
            <div key={item.method}>
              <span className="cashier-shift-summary-v2__payment-head">
                <strong>{paymentLabels[item.method][ar ? "ar" : "en"]}</strong>
                <StatusBadge tone="info">{item.paymentCount}</StatusBadge>
              </span>
              <span><small>{ar ? "تسوية المبيعات" : "Sale settlements"}</small><strong>{formatMoney(item.saleSettlementMinor, summary.currency)}</strong></span>
              <span><small>{ar ? "مبيعات آجلة" : "Credit sales"}</small><strong>{formatMoney(item.creditSalesMinor, summary.currency)}</strong></span>
              <span><small>{ar ? "العربونات" : "Deposits"}</small><strong>{formatMoney(item.depositMinor, summary.currency)}</strong></span>
              <span><small>{ar ? "تسويات أرصدة الطلبات المستقبلية" : "Future balance settlements"}</small><strong>{formatMoney(item.balanceSettlementMinor, summary.currency)}</strong></span>
              {(item.giftCardFundingMinor ?? 0) > 0 ? <span><small>{ar ? "تحصيل إصدار بطاقات الهدايا" : "Gift card issuance collections"}</small><strong>{formatMoney(item.giftCardFundingMinor!, summary.currency)}</strong></span> : null}
              <span><small>{ar ? "تحصيل ذمم" : "A/R settlements"}</small><strong>{formatMoney(item.receivableSettlementMinor, summary.currency)}</strong></span>
              <span><small>{ar ? "المسترد" : "Refunds"}</small><strong>{formatMoney(item.refundsMinor, summary.currency)}</strong></span>
              {(item.giftCardForfeitMinor ?? 0) > 0 ? <span><small>{ar ? "عربون بطاقة الهدية المحتفظ به" : "Retained gift card deposit"}</small><strong>{formatMoney(item.giftCardForfeitMinor!, summary.currency)}</strong></span> : null}
              <span className="is-total"><small>{ar ? "الصافي" : "Net"}</small><strong>{formatMoney(item.netMinor, summary.currency)}</strong></span>
            </div>
          )) : <small>{ar ? "لا توجد تحصيلات مسجلة حتى الآن." : "No collections recorded yet."}</small>}
        </div>
      </Card>

      <div className="cashier-shift-summary-v2__two-column">
        <Card title={ar ? "الطلبات المستقبلية والذمم" : "Future orders & receivables"}>
          <div className="cashier-shift-summary-v2__key-values">
            <span><small>{ar ? "الطلبات المستقبلية المكتملة" : "Completed future orders"}</small><strong>{summary.futureOrderCount}</strong></span>
            <span><small>{ar ? "عربونات" : "Deposits"}</small><strong>{formatMoney(summary.depositCollectedMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "تسوية أرصدة مستقبلية" : "Future balance settlements"}</small><strong>{formatMoney(summary.balanceSettlementCollectedMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "مبيعات آجلة جديدة" : "New credit sales"}</small><strong>{formatMoney(summary.creditSalesMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "تحصيل الذمم" : "A/R collected"}</small><strong>{formatMoney(summary.receivableSettlementCollectedMinor, summary.currency)}</strong></span>
          </div>
        </Card>
        <Card title={ar ? "الاستردادات وصافي الضريبة" : "Refunds & net VAT"}>
          <div className="cashier-shift-summary-v2__key-values">
            <span><small>{ar ? "استرداد صافي المبيعات" : "Refund net"}</small><strong>{formatMoney(summary.refundNetMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "ضريبة مستردة" : "Refund VAT"}</small><strong>{formatMoney(summary.refundTaxMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "استرداد العربون" : "Deposit refunds"}</small><strong>{formatMoney(summary.preOrderDepositRefundsMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "استرداد الرصيد" : "Balance refunds"}</small><strong>{formatMoney(summary.preOrderBalanceRefundsMinor, summary.currency)}</strong></span>
            <span><small>{ar ? "صافي الضريبة بعد الاسترداد" : "Net VAT after refunds"}</small><strong>{formatMoney(summary.netTaxAfterRefundsMinor, summary.currency)}</strong></span>
          </div>
        </Card>
      </div>
    </div>
  );
}
